import { useState } from "react";
import type { Identifier } from "ra-core";
import { ChevronDown, ChevronUp, RefreshCw, ScanSearch } from "lucide-react";

import { Button } from "@/components/ui/button";

import { FramingCoordinateInspector } from "./FramingCoordinateInspector";
import { useFramingSceneInspector } from "./useFramingSceneInspector";

export function FramingSceneInspectorPanel({
  sessionId,
  sceneId,
  attemptId,
  canViewTechnical,
}: {
  sessionId: Identifier;
  sceneId: string;
  attemptId?: string | null;
  canViewTechnical: boolean;
}) {
  const [open, setOpen] = useState(false);
  const enabled = open && canViewTechnical;
  const query = useFramingSceneInspector({
    sessionId,
    sceneId,
    attemptId,
    enabled,
  });

  if (!canViewTechnical) return null;

  return (
    <div className="mt-4 border-t pt-3">
      <Button
        type="button"
        variant="outline"
        size="sm"
        aria-expanded={open}
        aria-controls={`framing-inspector-${sceneId}`}
        onClick={() => setOpen((value) => !value)}
      >
        <ScanSearch className="size-3.5" />
        {open ? "Close coordinate inspector" : "Open coordinate inspector"}
        {open ? (
          <ChevronUp className="size-3.5" />
        ) : (
          <ChevronDown className="size-3.5" />
        )}
      </Button>

      {open ? (
        <div id={`framing-inspector-${sceneId}`} className="mt-3">
          {query.isLoading ? (
            <div
              className="rounded-md border bg-muted/30 p-4 text-sm text-muted-foreground"
              role="status"
            >
              Loading exact coordinate spaces and renderer evidence…
            </div>
          ) : query.error ? (
            <div
              className="rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 dark:bg-amber-950/30 dark:text-amber-200"
              role="status"
            >
              Detailed coordinate evidence is unavailable for this attempt. The
              v1 summary and event history above remain authoritative for the
              data they contain.
            </div>
          ) : query.data ? (
            <div className="space-y-3">
              <div className="flex justify-end">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => query.refetch()}
                  disabled={query.isFetching}
                >
                  <RefreshCw
                    className={`size-3.5 ${query.isFetching ? "animate-spin" : ""}`}
                  />
                  Refresh signed evidence
                </Button>
              </div>
              <FramingCoordinateInspector inspector={query.data} />
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
